import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    Box,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon
    from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon
    from "@mui/icons-material/ReceiptLongOutlined";
import SearchIcon
    from "@mui/icons-material/Search";
import {
    fetchAuthorInvoicesAPI,
    fetchBillingStatusesAPI,
    fetchOperatorInvoicesAPI,
    fetchPurchaserInvoicesAPI,
} from "../api/invoicing_api";
import {
    handleResponse,
} from "../util/integration";
import {
    POLLING_SPEED,
    usePolling,
} from "../util/polling";
import {
    PRIMARY_COLOR,
} from "../util/constants";
import {
    Invoicing,
} from "../entity/Invoice";
const LABEL = Object.freeze({
    title: "Rechnungen",
    searchPlaceholder:
        "Nach Rechnungsnummer suchen",
    clearSearch:
        "Suche löschen",
});
const MESSAGE = Object.freeze({
    noInvoices:
        "Keine Rechnungen vorhanden",
    noSearchResults:
        "Keine Rechnungen für diese Rechnungsnummer gefunden",
});
const CONFIG = Object.freeze({
    topSpacing: "50px",
    headerPadding: "25px",
    contentSpacing: 7,
    pageSpacing: 5,
    fullWidthColumns: 12,
    initialVisibleCount: 3,
    loadMoreStep: 2,
    loadMoreThreshold: 300,
    searchProgressSize: 18,
});
const CANCELLATION_PREFIX =
    "CANCELLATION_";
const EVENT = Object.freeze({
    scroll: "scroll",
});
const EVENT_OPTIONS = Object.freeze({
    passive: {
        passive: true,
    },
});
const STYLE = Object.freeze({
    topSpacing: {
        height:
            CONFIG.topSpacing,
    },
    titleContainer: {
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexShrink: 0,
    },
    titleIcon: {
        color: "white",
        fontSize: 30,
    },
    searchField: {
        width: {
            xs: "100%",
            sm: 380,
            md: 440,
        },
        "& .MuiOutlinedInput-root": {
            color: "white",
            backgroundColor:
                "rgba(255, 255, 255, 0.10)",
            "& fieldset": {
                borderColor:
                    "rgba(255, 255, 255, 0.45)",
            },
            "&:hover fieldset": {
                borderColor:
                    "rgba(255, 255, 255, 0.75)",
            },
            "&.Mui-focused fieldset": {
                borderColor:
                    "white",
            },
            "&.Mui-disabled": {
                backgroundColor:
                    "rgba(255, 255, 255, 0.06)",
            },
        },
        "& .MuiInputBase-input": {
            color: "white",
            "&::placeholder": {
                color:
                    "rgba(255, 255, 255, 0.75)",
                opacity: 1,
            },
            "&.Mui-disabled": {
                WebkitTextFillColor:
                    "rgba(255, 255, 255, 0.55)",
            },
        },
    },
    searchIcon: {
        color:
            "rgba(255, 255, 255, 0.85)",
    },
    clearSearchButton: {
        color: "white",
    },
    searchProgress: {
        color: "white",
    },
});
const COMPONENT_PROPS = Object.freeze({
    pageGrid: {
        container: true,
        spacing:
            CONFIG.pageSpacing,
    },
    contentGrid: {
        container: true,
        spacing:
            CONFIG.contentSpacing,
    },
    fullWidthGridItem: {
        item: true,
        xs:
            CONFIG.fullWidthColumns,
    },
    title: {
        variant: "h6",
        component: "h1",
    },
    searchField: {
        size: "small",
        inputProps: {
            "aria-label":
                LABEL.searchPlaceholder,
        },
    },
    startAdornment: {
        position: "start",
    },
    endAdornment: {
        position: "end",
    },
    searchProgress: {
        size:
            CONFIG.searchProgressSize,
    },
    clearSearchButton: {
        size: "small",
        "aria-label":
            LABEL.clearSearch,
    },
    clearSearchIcon: {
        fontSize: "small",
    },
});
const createColorStyles = (
    primaryColor,
) => Object.freeze({
    header: {
        p:
            CONFIG.headerPadding,
        backgroundColor:
            primaryColor,
        color: "white",
        display: "flex",
        flexDirection: {
            xs: "column",
            sm: "row",
        },
        alignItems: {
            xs: "stretch",
            sm: "center",
        },
        justifyContent:
            "space-between",
        gap: {
            xs: 2,
            sm: 3,
        },
    },
});
const COLOR_STYLE =
    createColorStyles(
        PRIMARY_COLOR,
    );
const hasBillingParticipation = (
    billingStatus,
) => Boolean(
    billingStatus?.isOperator
    || billingStatus?.isSeller
    || billingStatus?.isBuyer
);

const normalizeBillingStatuses = (
    response,
) => {
    if (!Array.isArray(response)) {
        return [];
    }
    const statusesById =
        new Map();
    response.forEach(
        (billingStatus) => {
            if (
                billingStatus === null
                || typeof billingStatus
                    !== "object"
                || billingStatus.id
                    === null
                || billingStatus.id
                    === undefined
            ) {
                return;
            }
            if (!hasBillingParticipation(
                billingStatus,
            )) {
                return;
            }
            statusesById.set(
                String(
                    billingStatus.id,
                ),
                billingStatus,
            );
        },
    );
    return Array.from(
        statusesById.values(),
    );
};
const normalizeSearchValue = (
    value,
) => {
    if (
        value === null
        || value === undefined
    ) {
        return "";
    }
    return String(value)
        .trim()
        .toLocaleLowerCase(
            "de-DE",
        )
        .replace(/\s+/g, "");
};
const getRegularInvoicePrefix = (
    invoice,
) => {
    const explicitPrefix = (
        invoice?.invoiceNoPrefix
        ?? invoice?.invoicePrefix
        ?? invoice?.issuerInvoicePrefix
    );
    if (
        explicitPrefix !== null
        && explicitPrefix !== undefined
    ) {
        return String(
            explicitPrefix,
        );
    }
    const cancellationPrefix =
        invoice
            ?.cancellationInvoiceNoPrefix;
    if (
        typeof cancellationPrefix
            === "string"
        && cancellationPrefix
            .startsWith(
                CANCELLATION_PREFIX,
            )
    ) {
        return cancellationPrefix
            .substring(
                CANCELLATION_PREFIX
                    .length,
            );
    }
    return "";
};
const getInvoiceSequentialNumber = (
    invoice,
) => {
    if (
        invoice?.invoiceNo === null
        || invoice?.invoiceNo
            === undefined
    ) {
        return "";
    }
    return String(
        invoice.invoiceNo,
    );
};
const getInvoiceNumber = (
    invoice,
) => {
    const prefix =
        getRegularInvoicePrefix(
            invoice,
        );
    const sequentialNumber =
        getInvoiceSequentialNumber(
            invoice,
        );
    return (
        `${prefix}${sequentialNumber}`
    );
};
const getCancellationInvoiceNumber = (
    invoice,
) => {
    const prefix = (
        invoice
            ?.cancellationInvoiceNoPrefix
        ?? (
            `${CANCELLATION_PREFIX}${
                getRegularInvoicePrefix(
                    invoice,
                )
            }`
        )
    );
    const sequentialNumber =
        getInvoiceSequentialNumber(
            invoice,
        );
    return (
        `${prefix}${sequentialNumber}`
    );
};
const hasCancellationInvoice = (
    invoice,
) => {
    return (
        invoice?.status
            === "CANCELLED"
        || (
            invoice
                ?.cancellationInvoicePdf
            !== null
            && invoice
                ?.cancellationInvoicePdf
            !== undefined
        )
        || (
            invoice
                ?.cancellationInvoiceCreationTimestamp
            !== null
            && invoice
                ?.cancellationInvoiceCreationTimestamp
            !== undefined
        )
        || (
            invoice
                ?.cancellationInvoiceAmount
            !== null
            && invoice
                ?.cancellationInvoiceAmount
            !== undefined
        )
    );
};
const collectInvoiceSearchValues = (
    response,
) => {
    if (!Array.isArray(response)) {
        return [];
    }
    const searchValues =
        new Set();
    response.forEach(
        (invoice) => {
            if (
                invoice === null
                || typeof invoice
                    !== "object"
            ) {
                return;
            }
            const invoiceNumber =
                normalizeSearchValue(
                    getInvoiceNumber(
                        invoice,
                    ),
                );
            if (invoiceNumber !== "") {
                searchValues.add(
                    invoiceNumber,
                );
            }
            if (
                hasCancellationInvoice(
                    invoice,
                )
            ) {
                const cancellationNumber =
                    normalizeSearchValue(
                        getCancellationInvoiceNumber(
                            invoice,
                        ),
                    );
                if (
                    cancellationNumber
                    !== ""
                ) {
                    searchValues.add(
                        cancellationNumber,
                    );
                }
            }
        },
    );
    return Array.from(
        searchValues,
    );
};
const createInvoiceRequestFactories = ({
    accessToken,
    billingStatus,
}) => {
    const requestFactories = [];
    const leadId = billingStatus.id;

    if (billingStatus.isSeller) {
        requestFactories.push(
            () => fetchAuthorInvoicesAPI(
                accessToken,
                leadId,
            ),
        );
    }

    if (billingStatus.isBuyer) {
        requestFactories.push(
            () => fetchPurchaserInvoicesAPI(
                accessToken,
                leadId,
            ),
        );
    }

    if (billingStatus.isOperator) {
        requestFactories.push(
            () => fetchOperatorInvoicesAPI(
                accessToken,
                leadId,
            ),
        );
    }

    return requestFactories;
};
const fetchInvoiceSearchValues = (
    createRequest,
) => {
    return new Promise(
        (resolve) => {
            let request;
            try {
                request =
                    createRequest();
            } catch (_error) {
                resolve([]);
                return;
            }
            try {
                handleResponse(
                    request,
                    (response) => {
                        try {
                            resolve(
                                collectInvoiceSearchValues(
                                    response,
                                ),
                            );
                        } catch (_error) {
                            resolve([]);
                        }
                    },
                    () => {
                        resolve([]);
                    },
                );
            } catch (_error) {
                resolve([]);
            }
        },
    );
};
const createInvoiceNumberIndex = async ({
    accessToken,
    billingStatusList,
}) => {
    const indexEntries =
        await Promise.all(
            billingStatusList.map(
                async (
                    billingStatus,
                ) => {
                    const leadId =
                        billingStatus.id;
                    const requestFactories =
                        createInvoiceRequestFactories({
                            accessToken,
                            billingStatus,
                        });
                    const searchValueLists =
                        await Promise.all(
                            requestFactories.map(
                                (
                                    createRequest,
                                ) => {
                                    return fetchInvoiceSearchValues(
                                        createRequest,
                                    );
                                },
                            ),
                        );
                    const invoiceNumbers =
                        new Set();
                    searchValueLists.forEach(
                        (searchValues) => {
                            searchValues.forEach(
                                (
                                    invoiceNumber,
                                ) => {
                                    invoiceNumbers.add(
                                        invoiceNumber,
                                    );
                                },
                            );
                        },
                    );
                    return [
                        String(
                            leadId,
                        ),
                        Array.from(
                            invoiceNumbers,
                        ),
                    ];
                },
            ),
        );
    return Object.fromEntries(
        indexEntries,
    );
};
const fetchBillingStatuses = ({
    accessToken,
    userId,
}) => {
    return new Promise(
        (resolve, reject) => {
            let request;
            try {
                request =
                    fetchBillingStatusesAPI(
                        accessToken,
                        userId,
                    );
            } catch (error) {
                reject(error);
                return;
            }
            try {
                handleResponse(
                    request,
                    (response) => {
                        resolve({
                            successful: true,
                            response,
                        });
                    },
                    (message) => {
                        resolve({
                            successful: false,
                            message,
                        });
                    },
                );
            } catch (error) {
                reject(error);
            }
        },
    );
};
const useInvoicePolling = ({
    accessToken,
    userId,
    showMessage,
}) => {
    const [
        billingStatusList,
        setBillingStatusList,
    ] = useState([]);
    const [
        invoiceNumberIndex,
        setInvoiceNumberIndex,
    ] = useState({});
    const [
        isLoading,
        setIsLoading,
    ] = useState(true);
    const [
        isSearchIndexLoading,
        setIsSearchIndexLoading,
    ] = useState(false);
    const hasUserId = (
        userId !== null
        && userId !== undefined
    );
    useEffect(() => {
        setBillingStatusList([]);
        setInvoiceNumberIndex({});
        setIsSearchIndexLoading(
            false,
        );
        setIsLoading(
            hasUserId,
        );
    }, [
        hasUserId,
        userId,
    ]);
    const pollingRestartKey =
        useMemo(
            () => ({
                accessToken,
                userId,
            }),
            [
                accessToken,
                userId,
            ],
        );
    const pollInvoices =
        useCallback(
            async ({
                isPollingActive,
            }) => {
                if (!hasUserId) {
                    return;
                }
                const result =
                    await fetchBillingStatuses({
                        accessToken,
                        userId,
                    });
                if (!isPollingActive()) {
                    return;
                }
                if (!result.successful) {
                    setIsLoading(
                        false,
                    );
                    setIsSearchIndexLoading(
                        false,
                    );
                    showMessage(
                        result.message,
                    );
                    return;
                }
                const nextBillingStatusList =
                    normalizeBillingStatuses(
                        result.response,
                    );
                setBillingStatusList(
                    nextBillingStatusList,
                );
                setIsLoading(
                    false,
                );
                if (
                    nextBillingStatusList
                        .length === 0
                ) {
                    setInvoiceNumberIndex(
                        {},
                    );
                    setIsSearchIndexLoading(
                        false,
                    );
                    return;
                }
                setIsSearchIndexLoading(
                    true,
                );
                try {
                    const nextInvoiceNumberIndex =
                        await createInvoiceNumberIndex({
                            accessToken,
                            billingStatusList:
                                nextBillingStatusList,
                        });
                    if (!isPollingActive()) {
                        return;
                    }
                    setInvoiceNumberIndex(
                        nextInvoiceNumberIndex,
                    );
                    setIsSearchIndexLoading(
                        false,
                    );
                } catch (error) {
                    if (!isPollingActive()) {
                        return;
                    }
                    setIsSearchIndexLoading(
                        false,
                    );
                    throw error;
                }
            },
            [
                accessToken,
                hasUserId,
                showMessage,
                userId,
            ],
        );
    const handlePollingError =
        useCallback(
            () => {
                setIsLoading(
                    false,
                );
                setIsSearchIndexLoading(
                    false,
                );
            },
            [],
        );
    usePolling({
        poll:
            pollInvoices,
        enabled:
            hasUserId,
        immediate:
            true,
        speed:
            POLLING_SPEED.slow,
        onError:
            handlePollingError,
        restartKey:
            pollingRestartKey,
    });
    return {
        billingStatusList,
        invoiceNumberIndex,
        isLoading,
        isSearchIndexLoading,
    };
};
const useInvoiceSearch = ({
    billingStatusList,
    invoiceNumberIndex,
    isSearchIndexLoading,
}) => {
    const [
        searchTerm,
        setSearchTerm,
    ] = useState("");
    const normalizedSearchTerm =
        useMemo(
            () => normalizeSearchValue(
                searchTerm,
            ),
            [
                searchTerm,
            ],
        );
    const hasSearchTerm =
        normalizedSearchTerm !== "";
    const hasSearchInput =
        searchTerm !== "";
    const hasSearchIndex = (
        Object.keys(
            invoiceNumberIndex,
        ).length > 0
    );
    const filteredBillingStatusList =
        useMemo(
            () => {
                if (!hasSearchTerm) {
                    return billingStatusList;
                }
                if (
                    isSearchIndexLoading
                    && !hasSearchIndex
                ) {
                    return [];
                }
                return billingStatusList.filter(
                    (billingStatus) => {
                        const invoiceNumbers = (
                            invoiceNumberIndex[
                                String(
                                    billingStatus.id,
                                )
                            ]
                            ?? []
                        );
                        return invoiceNumbers.some(
                            (invoiceNumber) => {
                                return invoiceNumber
                                    .includes(
                                        normalizedSearchTerm,
                                    );
                            },
                        );
                    },
                );
            },
            [
                billingStatusList,
                hasSearchIndex,
                hasSearchTerm,
                invoiceNumberIndex,
                isSearchIndexLoading,
                normalizedSearchTerm,
            ],
        );
    const handleSearchChange =
        useCallback(
            (event) => {
                setSearchTerm(
                    event.target.value,
                );
            },
            [],
        );
    const clearSearch =
        useCallback(
            () => {
                setSearchTerm("");
            },
            [],
        );
    const isSearchPending = (
        hasSearchTerm
        && isSearchIndexLoading
        && !hasSearchIndex
    );
    const hasBillingStatuses =
        billingStatusList.length > 0;
    const hasFilteredBillingStatuses =
        filteredBillingStatusList
            .length > 0;
    return {
        clearSearch,
        filteredBillingStatusList,
        handleSearchChange,
        hasBillingStatuses,
        hasFilteredBillingStatuses,
        hasSearchInput,
        hasSearchTerm,
        isSearchPending,
        normalizedSearchTerm,
        searchTerm,
    };
};
const useInvoicePagination = ({
    filteredBillingStatusList,
    isLoading,
    isSearchPending,
    resetKey,
    userId,
}) => {
    const [
        visibleCount,
        setVisibleCount,
    ] = useState(
        CONFIG.initialVisibleCount,
    );
    useEffect(() => {
        setVisibleCount(
            CONFIG.initialVisibleCount,
        );
    }, [
        resetKey,
        userId,
    ]);
    const visibleBillingStatuses =
        useMemo(
            () => {
                return filteredBillingStatusList
                    .slice(
                        0,
                        visibleCount,
                    );
            },
            [
                filteredBillingStatusList,
                visibleCount,
            ],
        );
    const hasMoreBillingStatuses = (
        visibleCount
        < filteredBillingStatusList.length
    );
    useEffect(() => {
        if (
            isLoading
            || isSearchPending
            || !hasMoreBillingStatuses
        ) {
            return undefined;
        }
        const loadMoreIfNecessary =
            () => {
                const viewportBottom =
                    window.scrollY
                    + window.innerHeight;
                const pageBottom =
                    document.documentElement
                        .scrollHeight;
                const remainingDistance =
                    pageBottom
                    - viewportBottom;
                if (
                    remainingDistance
                    > CONFIG
                        .loadMoreThreshold
                ) {
                    return;
                }
                setVisibleCount(
                    (currentCount) => {
                        return Math.min(
                            currentCount
                            + CONFIG
                                .loadMoreStep,
                            filteredBillingStatusList
                                .length,
                        );
                    },
                );
            };
        window.addEventListener(
            EVENT.scroll,
            loadMoreIfNecessary,
            EVENT_OPTIONS.passive,
        );
        loadMoreIfNecessary();
        return () => {
            window.removeEventListener(
                EVENT.scroll,
                loadMoreIfNecessary,
            );
        };
    }, [
        filteredBillingStatusList.length,
        hasMoreBillingStatuses,
        isLoading,
        isSearchPending,
    ]);
    return {
        visibleBillingStatuses,
    };
};
const useInvoices = ({
    accessToken,
    userId,
    showMessage,
}) => {
    const {
        billingStatusList,
        invoiceNumberIndex,
        isLoading,
        isSearchIndexLoading,
    } = useInvoicePolling({
        accessToken,
        userId,
        showMessage,
    });
    const {
        clearSearch,
        filteredBillingStatusList,
        handleSearchChange,
        hasBillingStatuses,
        hasFilteredBillingStatuses,
        hasSearchInput,
        hasSearchTerm,
        isSearchPending,
        normalizedSearchTerm,
        searchTerm,
    } = useInvoiceSearch({
        billingStatusList,
        invoiceNumberIndex,
        isSearchIndexLoading,
    });
    const {
        visibleBillingStatuses,
    } = useInvoicePagination({
        filteredBillingStatusList,
        isLoading,
        isSearchPending,
        resetKey:
            normalizedSearchTerm,
        userId,
    });
    return {
        clearSearch,
        handleSearchChange,
        hasBillingStatuses,
        hasFilteredBillingStatuses,
        hasSearchInput,
        hasSearchTerm,
        isLoading,
        isSearchIndexLoading,
        isSearchPending,
        searchTerm,
        visibleBillingStatuses,
        isSearchDisabled: (
            isLoading
            || !hasBillingStatuses
        ),
    };
};
function SearchStartAdornment() {
    return (
        <InputAdornment
            {...COMPONENT_PROPS.startAdornment}
        >
            <SearchIcon
                sx={STYLE.searchIcon}
            />
        </InputAdornment>
    );
}
function SearchEndAdornment({
    clearSearch,
    hasSearchInput,
    isSearchIndexLoading,
}) {
    if (isSearchIndexLoading) {
        return (
            <InputAdornment
                {...COMPONENT_PROPS.endAdornment}
            >
                <CircularProgress
                    {...COMPONENT_PROPS.searchProgress}
                    sx={STYLE.searchProgress}
                />
            </InputAdornment>
        );
    }
    if (!hasSearchInput) {
        return null;
    }
    return (
        <InputAdornment
            {...COMPONENT_PROPS.endAdornment}
        >
            <IconButton
                {...COMPONENT_PROPS.clearSearchButton}
                onClick={clearSearch}
                sx={STYLE.clearSearchButton}
            >
                <CloseIcon
                    {...COMPONENT_PROPS.clearSearchIcon}
                />
            </IconButton>
        </InputAdornment>
    );
}
const createSearchInputProps = ({
    clearSearch,
    hasSearchInput,
    isSearchIndexLoading,
}) => {
    return {
        startAdornment: (
            <SearchStartAdornment />
        ),
        endAdornment: (
            <SearchEndAdornment
                clearSearch={clearSearch}
                hasSearchInput={hasSearchInput}
                isSearchIndexLoading={
                    isSearchIndexLoading
                }
            />
        ),
    };
};
export default function Invoices({
    appAPI,
    contextAPI,
}) {
    const accessToken =
        contextAPI.password;
    const userId =
        contextAPI.id;
    const showMessage =
        appAPI.showMessage;
    const {
        clearSearch,
        handleSearchChange,
        hasBillingStatuses,
        hasFilteredBillingStatuses,
        hasSearchInput,
        hasSearchTerm,
        isLoading,
        isSearchDisabled,
        isSearchIndexLoading,
        isSearchPending,
        searchTerm,
        visibleBillingStatuses,
    } = useInvoices({
        accessToken,
        userId,
        showMessage,
    });
    const searchInputProps =
        useMemo(
            () => createSearchInputProps({
                clearSearch,
                hasSearchInput,
                isSearchIndexLoading,
            }),
            [
                clearSearch,
                hasSearchInput,
                isSearchIndexLoading,
            ],
        );
    return (
        <Grid
            {...COMPONENT_PROPS.pageGrid}
        >
            <Grid
                {...COMPONENT_PROPS.fullWidthGridItem}
            >
                <Box
                    sx={STYLE.topSpacing}
                />
            </Grid>
            <Grid
                {...COMPONENT_PROPS.fullWidthGridItem}
            >
                <Grid
                    {...COMPONENT_PROPS.contentGrid}
                >
                    <Grid
                        {...COMPONENT_PROPS.fullWidthGridItem}
                    >
                        <Paper
                            sx={COLOR_STYLE.header}
                        >
                            <Box
                                sx={STYLE.titleContainer}
                            >
                                <ReceiptLongOutlinedIcon
                                    sx={STYLE.titleIcon}
                                />
                                <Typography
                                    {...COMPONENT_PROPS.title}
                                >
                                    {LABEL.title}
                                </Typography>
                            </Box>
                            <TextField
                                {...COMPONENT_PROPS.searchField}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder={
                                    LABEL.searchPlaceholder
                                }
                                disabled={isSearchDisabled}
                                InputProps={searchInputProps}
                                sx={STYLE.searchField}
                            />
                        </Paper>
                    </Grid>
                    {isLoading && (
                        <Grid
                            {...COMPONENT_PROPS.fullWidthGridItem}
                        >
                            <LinearProgress />
                        </Grid>
                    )}
                    {isSearchPending && (
                        <Grid
                            {...COMPONENT_PROPS.fullWidthGridItem}
                        >
                            <LinearProgress />
                        </Grid>
                    )}
                    {!isLoading
                        && !isSearchPending
                        && hasFilteredBillingStatuses
                        && visibleBillingStatuses.map(
                            (billingStatus) => (
                                <Grid
                                    {...COMPONENT_PROPS.fullWidthGridItem}
                                    key={
                                        billingStatus.id
                                    }
                                >
                                    <Invoicing
                                        appAPI={appAPI}
                                        contextAPI={contextAPI}
                                        billableItem={
                                            billingStatus
                                        }
                                    />
                                </Grid>
                            ),
                        )}
                    {!isLoading
                        && !isSearchPending
                        && !hasBillingStatuses
                        && (
                            <Grid
                                {...COMPONENT_PROPS.fullWidthGridItem}
                            >
                                <Typography>
                                    {MESSAGE.noInvoices}
                                </Typography>
                            </Grid>
                        )}
                    {!isLoading
                        && !isSearchPending
                        && hasBillingStatuses
                        && hasSearchTerm
                        && !hasFilteredBillingStatuses
                        && (
                            <Grid
                                {...COMPONENT_PROPS.fullWidthGridItem}
                            >
                                <Typography>
                                    {MESSAGE.noSearchResults}
                                </Typography>
                            </Grid>
                        )}
                </Grid>
            </Grid>
        </Grid>
    );
}